import { useMutation } from '@tanstack/react-query';
import { rpcClient } from '@/api/rpc/client';
import { useToast } from '@/context/ToastContext';
import type { UiBugReportContext } from '@erp/rpc';

/** The page/browser/user context the legacy email-only report carries. */
export function buildUiBugReportContext(currentUser: {
  id: string;
  name: string;
  emails: string[];
  organizationId: string;
  organizationName: string;
}): UiBugReportContext {
  return {
    userId: currentUser.id,
    userName: currentUser.name,
    userEmail: currentUser.emails[0] ?? null,
    organizationId: currentUser.organizationId,
    organizationName: currentUser.organizationName,
    pageUrl: window.location.href,
    pageTitle: document.title,
    userAgent: navigator.userAgent,
    screenWidth: window.screen.width,
    screenHeight: window.screen.height,
    viewportWidth: window.innerWidth,
    viewportHeight: window.innerHeight,
    timestamp: new Date().toISOString(),
  };
}

export interface SubmitUiBugReportInput {
  message: string;
  screenshotDataUrl?: string | null;
  context: UiBugReportContext;
}

export function useSubmitUiBugReport() {
  const { showToast } = useToast();

  return useMutation<{ success: boolean }, Error, SubmitUiBugReportInput>({
    mutationFn: async (input) => {
      return await rpcClient.core.uiBugReport.submit(input);
    },
    onSuccess: () => {
      showToast({
        title: 'Feedback sent',
        message: 'Thanks for helping us improve the product.',
        color: 'success',
        icon: 'check',
      });
    },
    onError: (error) => {
      showToast({
        title: 'Could not send feedback',
        message: error.message,
        color: 'error',
        icon: 'x',
      });
    },
  });
}
